import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PracticeCard = ({ practice, isSelected, onSelect, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'low': return 'text-success bg-success/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'high': return 'text-destructive bg-destructive/10';
      case 'expert': return 'text-primary bg-primary/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getComplexityLabel = (complexity) => {
    switch (complexity) {
      case 'low': return 'Basic';
      case 'medium': return 'Standard';
      case 'high': return 'Advanced';
      case 'expert': return 'Expert';
      default: return 'Unknown';
    }
  };

  return (
    <div className={`bg-card border rounded-lg p-6 hover-lift nav-transition ${
      isSelected ? 'border-primary shadow-elevation-2' : 'border-border hover:border-primary/50'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2">
            <div className="bg-primary/10 text-primary px-2 py-1 rounded text-sm font-mono font-medium">
              {practice?.code}
            </div>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(practice?.complexity)}`}>
              {getComplexityLabel(practice?.complexity)}
            </div>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1 line-clamp-2">
            {practice?.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {practice?.description}
          </p>
        </div>
        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted nav-transition"
            title={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            <Icon 
              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
              size={20} 
            />
          </button>
        </div>
      </div>
      {/* Metadata */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={16} className="text-muted-foreground" />
          <span className="text-muted-foreground">Timeline:</span>
          <span className="font-medium text-foreground">{practice?.timeline}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Tag" size={16} className="text-muted-foreground" />
          <span className="text-muted-foreground">Version:</span>
          <span className="font-medium text-foreground">{practice?.version}</span>
        </div>
      </div>
      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-border pt-4 space-y-4">
          {/* Key Benefits */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center space-x-2">
              <Icon name="CheckCircle2" size={16} className="text-success" />
              <span>Key Benefits</span>
            </h4>
            <ul className="space-y-1">
              {practice?.benefits?.map((benefit, index) => (
                <li key={index} className="text-sm text-muted-foreground flex items-start space-x-2">
                  <Icon name="ArrowRight" size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Prerequisites */}
          {practice?.prerequisites?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-warning" />
                <span>Prerequisites</span>
              </h4>
              <ul className="space-y-1">
                {practice?.prerequisites?.map((prereq, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-start space-x-2">
                    <Icon name="ArrowRight" size={14} className="mt-0.5 flex-shrink-0" />
                    <span>{prereq}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Integration Requirements */}
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center space-x-2">
              <Icon name="Zap" size={16} className="text-accent" />
              <span>Integration Points</span>
            </h4>
            <div className="flex flex-wrap gap-2">
              {practice?.integrations?.map((integration, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs"
                >
                  {integration}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewDetails(practice)}
          iconName="ExternalLink"
          iconPosition="right"
        >
          View Process Flow
        </Button>
        
        <Button
          variant={isSelected ? "outline" : "default"}
          size="sm"
          onClick={() => onSelect(practice)}
          iconName={isSelected ? "Check" : "Plus"}
          iconPosition="left"
        >
          {isSelected ? 'Selected' : 'Add to Engagement'}
        </Button>
      </div>
    </div>
  );
};

export default PracticeCard;